import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import { FaFilter, FaTimes } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const { products, loading, pages } = useProducts(category, search, page);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100000]);

  const handlePageChange = (newPage) => {
    setSearchParams({ ...Object.fromEntries(searchParams), page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];
    if (selectedMaterials.length > 0) {
      filtered = filtered.filter(p => selectedMaterials.includes(p.material));
    }
    filtered = filtered.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return filtered;
  }, [products, selectedMaterials, priceRange, sortBy]);

  const handleMaterialChange = (material) => {
    setSelectedMaterials(prev =>
      prev.includes(material) ? prev.filter(m => m !== material) : [...prev, material]
    );
  };

  const handleClearFilters = () => {
    setSelectedMaterials([]);
    setPriceRange([0, 100000]);
    setSortBy('newest');
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen bg-deepDark pt-10 pb-20"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-5xl md:text-6xl font-serif text-white mb-6 tracking-wide"
          >
            {category ? `${category} Collection` : 'The Collection'}
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="w-16 h-[1px] bg-gold mx-auto mb-6"
          ></motion.div>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-textMuted text-sm font-light tracking-widest uppercase"
          >
            Discover our exquisite pieces of high jewelry
          </motion.p>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="md:hidden flex items-center justify-center w-full gap-3 mb-8 border border-gold text-gold p-4 text-xs tracking-widest uppercase hover:bg-gold hover:text-deepDark transition-colors duration-300"
        >
          <FaFilter /> Refine Selection
        </button>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Filters Sidebar */}
          <aside className={`${showFilters ? 'fixed inset-0 z-50 bg-deepDark/95 backdrop-blur-xl flex flex-col p-6 overflow-y-auto' : 'hidden md:block'} md:relative md:inset-auto md:z-auto`}>
            <div className="md:sticky md:top-32 space-y-10">
              <div className="flex items-center justify-between md:hidden mb-8">
                <h3 className="text-lg font-serif text-white tracking-widest uppercase">Refine</h3>
                <button onClick={() => setShowFilters(false)} className="text-textMuted hover:text-gold transition">
                  <FaTimes size={24} />
                </button>
              </div>

              {(selectedMaterials.length > 0 || priceRange[0] > 0 || priceRange[1] < 100000 || sortBy !== 'newest') && (
                <button
                  onClick={handleClearFilters}
                  className="w-full text-xs text-gold uppercase tracking-widest border border-gold/30 py-3 hover:bg-gold hover:text-deepDark transition-colors duration-300"
                >
                  Clear Refinements
                </button>
              )}

              <div>
                <h4 className="text-white text-sm font-semibold tracking-widest uppercase mb-6 flex items-center gap-4">
                  Material
                  <span className="flex-1 h-[1px] bg-white/10"></span>
                </h4>
                <div className="space-y-4">
                  {['Gold', 'Silver', 'Platinum', 'Diamond'].map((mat) => (
                    <label key={mat} onClick={() => handleMaterialChange(mat)} className="flex items-center text-textMuted hover:text-white transition-colors duration-300 cursor-pointer group">
                      <div className={`w-4 h-4 mr-4 border transition-colors flex items-center justify-center ${selectedMaterials.includes(mat) ? 'border-gold bg-gold' : 'border-white/30 group-hover:border-gold'}`}>
                        {selectedMaterials.includes(mat) && <div className="w-1.5 h-1.5 bg-deepDark"></div>}
                      </div>
                      <span className="text-sm font-light tracking-wider">{mat}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-white text-sm font-semibold tracking-widest uppercase mb-6 flex items-center gap-4">
                  Investment Range
                  <span className="flex-1 h-[1px] bg-white/10"></span>
                </h4>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-[10px] text-textMuted uppercase tracking-widest mb-2">Min (₹)</label>
                    <input
                      type="number"
                      min="0" max="100000" step="1000"
                      value={priceRange[0]}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setPriceRange([!isNaN(val) ? val : 0, priceRange[1]]);
                      }}
                      className="w-full bg-surfaceDark border border-white/10 text-white rounded-none px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors font-light"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] text-textMuted uppercase tracking-widest mb-2">Max (₹)</label>
                    <input
                      type="number"
                      min="0" max="100000" step="1000"
                      value={priceRange[1]}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setPriceRange([priceRange[0], !isNaN(val) ? val : 100000]);
                      }}
                      className="w-full bg-surfaceDark border border-white/10 text-white rounded-none px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors font-light"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-white text-sm font-semibold tracking-widest uppercase mb-6 flex items-center gap-4">
                  Sort By
                  <span className="flex-1 h-[1px] bg-white/10"></span>
                </h4>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-surfaceDark border border-white/10 text-white rounded-none px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors font-light appearance-none"
                >
                  <option value="newest">Latest Additions</option>
                  <option value="price-high">Price: Haute to Accessible</option>
                  <option value="price-low">Price: Accessible to Haute</option>
                  <option value="rating">Client Favorites</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Main Products Area */}
          <div className="md:col-span-3">
            {loading ? (
              <div className="text-center py-20 flex flex-col items-center">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-textMuted text-xs tracking-widest uppercase">Curating Collection...</p>
              </div>
            ) : filteredAndSortedProducts.length > 0 ? (
              <>
                <div className="text-xs text-textMuted tracking-widest uppercase mb-8 border-b border-white/5 pb-4">
                  {filteredAndSortedProducts.length} Piece{filteredAndSortedProducts.length !== 1 ? 's' : ''} Discovered
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {filteredAndSortedProducts.map((product, idx) => (
                    <motion.div
                      key={product._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1, duration: 0.6 }}
                    >
                      <ProductCard product={product} />
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex justify-center items-center gap-8 mt-20 pt-10 border-t border-white/5">
                  {page > 1 ? (
                    <button onClick={() => handlePageChange(page - 1)} className="text-white hover:text-gold text-xs tracking-widest uppercase transition-colors">Previous</button>
                  ) : <span className="text-transparent text-xs tracking-widest uppercase">Previous</span>}
                  
                  <span className="text-textMuted font-serif italic text-lg">{page} / {pages}</span>
                  
                  {page < pages ? (
                    <button onClick={() => handlePageChange(page + 1)} className="text-white hover:text-gold text-xs tracking-widest uppercase transition-colors">Next</button>
                  ) : <span className="text-transparent text-xs tracking-widest uppercase">Next</span>}
                </div>
              </>
            ) : (
              <div className="py-20 text-center border border-white/5 bg-surfaceDark/50">
                <p className="text-textMuted text-sm font-light tracking-wide mb-6">No pieces match your current refined criteria.</p>
                <button onClick={handleClearFilters} className="border border-gold text-gold px-8 py-3 text-xs tracking-widest uppercase hover:bg-gold hover:text-deepDark transition-colors">
                  Reset Selection
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductsPage;
